import React, { useState } from 'react';
import axios from 'axios';
import { Star, Send, X, Camera, Store, BadgeCheck } from 'lucide-react';
import { usePageT } from '../i18n/usePageT';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReviewForm = ({ productId, productName, onReviewSubmitted, trigger }) => {
  const { language } = useLanguage();
  const { token, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const addPhotos = (files) => {
    const remaining = 3 - photos.length;
    Array.from(files).slice(0, remaining).forEach((file) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const scale = Math.min(1, 900 / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
        URL.revokeObjectURL(url);
        setPhotos((prev) => (prev.length < 3 ? [...prev, dataUrl] : prev));
      };
      img.src = url;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error(language === 'fr' ? 'Connectez-vous pour laisser un avis' : 'Login to leave a review');
      return;
    }

    if (rating === 0) {
      toast.error(language === 'fr' ? 'Veuillez donner une note' : 'Please give a rating');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId,
        rating,
        title,
        content,
        photos
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(language === 'fr' ? 'Avis publié!' : 'Review submitted!');
      setOpen(false);
      setRating(0);
      setTitle('');
      setContent('');
      setPhotos([]);
      onReviewSubmitted?.();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(language === 'fr' ? 'Vous avez déjà laissé un avis' : 'You already reviewed this product');
      } else {
        toast.error(error.response?.data?.detail || 'Error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" data-testid="write-review-btn">
            <Star className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Écrire un avis' : 'Write a Review'}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {language === 'fr' ? 'Écrire un avis' : 'Write a Review'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* Product Name */}
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Produit' : 'Product'}</p>
            <p className="font-medium">{productName}</p>
          </div>

          {/* Star Rating */}
          <div>
            <Label>{language === 'fr' ? 'Note' : 'Rating'} *</Label>
            <div className="flex items-center gap-1 mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`w-8 h-8 transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'fill-[#E9C46A] text-[#E9C46A]'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
              <span className="ml-3 text-lg font-semibold">
                {rating > 0 ? rating : '-'}/5
              </span>
            </div>
          </div>

          {/* Review Title */}
          <div>
            <Label htmlFor="review-title">
              {language === 'fr' ? 'Titre de l\'avis' : 'Review Title'} *
            </Label>
            <Input
              id="review-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={language === 'fr' ? 'Résumez votre expérience' : 'Summarize your experience'}
              className="mt-1"
              required
              data-testid="review-title-input"
            />
          </div>

          {/* Review Content */}
          <div>
            <Label htmlFor="review-content">
              {language === 'fr' ? 'Votre avis' : 'Your Review'} *
            </Label>
            <Textarea
              id="review-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={language === 'fr' ? 'Partagez votre expérience avec ce produit...' : 'Share your experience with this product...'}
              className="mt-1"
              rows={4}
              required
              data-testid="review-content-input"
            />
          </div>

          {/* Photos */}
          <div>
            <Label>{language === 'fr' ? 'Photos (optionnel, max 3)' : 'Photos (optional, max 3)'}</Label>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {photos.map((p, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border" data-testid={`review-photo-preview-${i}`}>
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                    className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
                    data-testid={`review-photo-remove-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {photos.length < 3 && (
                <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#0F4C5C] transition-colors" data-testid="review-photo-add">
                  <Camera className="w-5 h-5 text-gray-400" />
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }}
                    data-testid="review-photo-input"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="submit-review-btn">
              {loading ? (
                language === 'fr' ? 'Envoi...' : 'Submitting...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Publier l\'avis' : 'Submit Review'}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Review List Component
export const ReviewList = ({ reviews, loading, canReply = false, onReplied }) => {
  const { language } = useLanguage();
  const T = usePageT();
  const { token } = useAuth();
  const [lightbox, setLightbox] = useState(null);
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  const submitReply = async (reviewId) => {
    if (!replyText.trim()) return;
    setSubmittingReply(true);
    try {
      await axios.post(`${API}/reviews/${reviewId}/reply`, { content: replyText.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(T('Réponse publiée !', 'Reply published!'));
      setReplyingId(null);
      setReplyText('');
      onReplied?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSubmittingReply(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gray-200 rounded-full" />
              <div className="h-4 bg-gray-200 rounded w-32" />
            </div>
            <div className="h-3 bg-gray-200 rounded w-full mb-2" />
            <div className="h-3 bg-gray-200 rounded w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  if (!reviews || reviews.length === 0) {
    return (
      <div className="text-center py-8">
        <Star className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-muted-foreground">
          {language === 'fr' ? 'Aucun avis pour le moment' : 'No reviews yet'}
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          {language === 'fr' ? 'Soyez le premier à donner votre avis!' : 'Be the first to review!'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {reviews.map((review) => (
        <div key={review.id} className="border-b border-border pb-6 last:border-0" data-testid={`review-${review.id}`}>
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#0F4C5C]/10 flex items-center justify-center flex-shrink-0">
              <span className="font-semibold text-[#0F4C5C]">
                {review.user_name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{review.user_name}</span>
                {review.verified_purchase && (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                    data-testid={`verified-badge-${review.id}`}
                  >
                    <BadgeCheck className="w-3 h-3" />
                    {T('Achat vérifié', 'Verified purchase')}
                  </span>
                )}
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < review.rating ? 'fill-[#E9C46A] text-[#E9C46A]' : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground">
                  {new Date(review.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                </span>
              </div>
              <h4 className="font-semibold mt-2">{review.title}</h4>
              <p className="text-muted-foreground mt-1">{review.content}</p>
              {review.photos?.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {review.photos.map((p, i) => (
                    <img
                      key={i}
                      src={p}
                      alt=""
                      loading="lazy"
                      onClick={() => setLightbox(p)}
                      className="w-20 h-20 rounded-lg object-cover border cursor-zoom-in hover:opacity-90 transition-opacity"
                      data-testid={`review-photo-${review.id}-${i}`}
                    />
                  ))}
                </div>
              )}
              {review.reply && (
                <div className="mt-3 bg-[#0F4C5C]/5 border-l-2 border-[#0F4C5C] rounded-r-lg p-3" data-testid={`review-reply-${review.id}`}>
                  <p className="text-xs font-semibold text-[#0F4C5C] flex items-center gap-1.5 mb-1">
                    <Store className="w-3.5 h-3.5" />
                    {T('Réponse du vendeur', 'Seller response')} — {review.reply.vendor_name}
                  </p>
                  <p className="text-sm text-[#0F4C5C]/80">{review.reply.content}</p>
                </div>
              )}
              {canReply && !review.reply && (
                replyingId === review.id ? (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={3}
                      placeholder={T('Votre réponse publique au client…', 'Your public reply to the customer…')}
                      data-testid={`review-reply-input-${review.id}`}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => submitReply(review.id)} disabled={submittingReply} className="btn-primary" data-testid={`review-reply-submit-${review.id}`}>
                        {T('Publier la réponse', 'Publish reply')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setReplyingId(null); setReplyText(''); }} data-testid={`review-reply-cancel-${review.id}`}>
                        {T('Annuler', 'Cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setReplyingId(review.id)}
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F4C5C] hover:text-[#E9C46A] transition-colors"
                    data-testid={`review-reply-btn-${review.id}`}
                  >
                    <Store className="w-4 h-4" />
                    {T('Répondre à cet avis', 'Reply to this review')}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      ))}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-2xl p-2">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          {lightbox && <img src={lightbox} alt="" className="w-full rounded-lg" data-testid="review-photo-lightbox" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Review Summary Component
export const ReviewSummary = ({ rating, reviewCount, ratingDistribution }) => {
  const { language } = useLanguage();

  // Calculate distribution percentages
  const distribution = ratingDistribution || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  const total = Object.values(distribution).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="flex flex-col md:flex-row gap-8 mb-8">
      {/* Overall Rating */}
      <div className="text-center">
        <div className="text-5xl font-bold text-[#0F4C5C]">{rating?.toFixed(1) || '0.0'}</div>
        <div className="flex justify-center mt-2">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-5 h-5 ${
                i < Math.floor(rating || 0) ? 'fill-[#E9C46A] text-[#E9C46A]' : 'text-gray-300'
              }`}
            />
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {reviewCount} {language === 'fr' ? 'avis' : 'reviews'}
        </p>
      </div>

      {/* Rating Distribution */}
      <div className="flex-1 space-y-2">
        {[5, 4, 3, 2, 1].map((stars) => {
          const count = distribution[stars] || 0;
          const percentage = (count / total) * 100;
          return (
            <div key={stars} className="flex items-center gap-3">
              <span className="text-sm w-12">{stars} {language === 'fr' ? 'étoiles' : 'stars'}</span>
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#E9C46A] rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-sm text-muted-foreground w-8">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
